import React, { useEffect, useState, useCallback } from "react";
import { userApi, apiError, API } from "@/lib/userApi";
import { toast } from "sonner";
import { Loader2, RefreshCw, Download, PencilRuler } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { Link } from "react-router-dom";

const ACTIVE = ["QUEUED", "PROCESSING", "ANALYZING", "OCR", "DETECTING", "RECONSTRUCTING", "EXPORTING"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const UserJobs = () => {
  const [jobs, setJobs] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await userApi.get("/user/jobs");
      setJobs(data);
    } catch (e) {
      toast.error(apiError(e, "No se pudieron cargar tus trabajos"));
      setJobs([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!jobs?.some((j) => ACTIVE.includes(j.status))) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [jobs, load]);

  if (!jobs) return <Loader2 className="w-6 h-6 animate-spin text-red-400" />;

  return (
    <div data-testid="user-jobs">
      <button data-testid="user-jobs-refresh" onClick={load}
        className="flex items-center gap-2 text-xs text-white/60 hover:text-white mb-4">
        <RefreshCw className="w-3.5 h-3.5" /> Actualizar
      </button>
      {jobs.length === 0 && (
        <div className="glass-card p-6 text-sm text-white/60">
          Todavía no tienes trabajos. Sube una imagen o un PDF para empezar.
        </div>
      )}
      <div className="space-y-3">
        {jobs.map((j) => (
          <div key={j.job_id} className="glass-card p-4" data-testid={`user-job-${j.job_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white text-sm font-semibold truncate">{j.filename}</div>
                <div className="text-[11px] text-white/45 font-mono mt-0.5">
                  {j.job_id.slice(0, 10)} · {j.created_at?.slice(0, 16).replace("T", " ")} · {j.cost} créditos
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status={j.status} />
                {ACTIVE.includes(j.status) && <span className="text-xs text-white/60">{j.progress}%</span>}
              </div>
            </div>
            {j.error && <div className="text-xs text-rose-300 mt-2">{j.error}</div>}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {j.editor_url && (
                <Link to={j.editor_url} data-testid={`user-job-edit-${j.job_id}`}
                  className="glass-btn glass-btn-red inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs">
                  <PencilRuler className="w-3.5 h-3.5" /> Abrir editor
                </Link>
              )}
              {Object.entries(j.downloads || {}).map(([name, url]) => (
                <a key={name} href={`${BACKEND}${url}`} target="_blank" rel="noreferrer"
                  data-testid={`user-job-file-${name}`}
                  className="glass-btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs">
                  <Download className="w-3.5 h-3.5" /> {name}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-white/35 mt-4 font-mono">{API}</p>
    </div>
  );
};

export default UserJobs;
